import { useEffect, useMemo, useState } from "react";
import { useAlert } from "react-alert";
import { AiOutlineSend } from "react-icons/ai";
import firebase from 'database/FirebaseConfig';
import axios from 'axios';
import checkbox from 'assets/icons/checkboxBlue.svg';
import { confirmAlert } from 'react-confirm-alert';
import { BsFillTrashFill, BsPencilSquare } from "react-icons/bs";
import { ResetPassword } from "components/ResetPassword";
import styled from "styled-components";
import { UserProps } from "components/ManagerAccounts";
import MaskCPFCNPJ from "utils/MaskCpfCnpj";
import { Companies } from "Page/PainelCompanieUsers";

import './index.scss';
import { Colors, Links } from "utils/template";
import { useAuth } from "ctx/AuthContext";

const StyledResetPassword = styled(ResetPassword)`
  height: 35px;
`;

type AccountProps = {
  user: UserProps;
  themeHex?: string;
  company: Companies;
  closeModalUsers: () => void;
  deleteUserOnCompany?: (newUsers: any) => void;
};

export function Account({ user, themeHex, company, closeModalUsers, deleteUserOnCompany }: AccountProps) {
  const [name, setName] = useState(user?.name ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [changeItem, setChangeItem] = useState(false);
  const [document, setDocument] = useState(String(user?.document ?? ''));
  const alert = useAlert();
  const color = themeHex ? themeHex : Colors.secondary;
  const db = firebase.firestore();
  const [toggleCheckBox, setToggleCheckBox] = useState(!!user?.access?.delivery);
  const { userInfo } = useAuth();

  // Sempre derive a versão sem formatação do estado `document`
  const documentWithoutFormatation = useMemo(
    () => String(document ?? '').replace(/[.\-]/g, ''),
    [document]
  );

  useEffect(() => {
    setName(user?.name ?? '');
    setEmail(user?.email ?? '');
    setDocument(String(user?.document ?? ''));
    setToggleCheckBox(!!user?.access?.delivery);
  }, [user]);

  const GetCompany = async (uid: string) => {
    const db = firebase.firestore();
    try {
      const doc = await db.collection('companies').doc(uid).get();
      const companyInfo = doc.data();
      return companyInfo as Companies | undefined;
    } catch (err) {
      console.log("Não foi possível pegar a company", err);
    }
  };

  async function handleAccess(uid: string, value: number) {
    if (!uid || !company?.uid) {
      alert.error('Dados insuficientes para alterar acesso.');
      return;
    }

    if (value === 0) {
      const waybillsSnapshot = await db.collection('waybills').where('company', '==', company.uid).get();
      const waybillsByCompanyData = waybillsSnapshot.docs.map(doc => doc.data() as any);
      const driverInRomaneio = waybillsByCompanyData.find(waybill => waybill.driver === user.name && waybill.status !== 4);
      if (!!driverInRomaneio) {
        alert.error('Não foi possível excluir o motorista porque ele está associado ao romaneio');
        return;
      }
    }

    setToggleCheckBox(prevState => !prevState);

    try {
      await db.collection('users').doc(uid).update({
        access: {
          ...(user?.access ?? {}),
          delivery: value,
        }
      }).then(() => {
        if (value === 1) {
          alert.success("ACESSO LIBERADO!");
        } else {
          alert.success("ACESSO BLOQUEADO!");
        }
        console.log('Profile Successfully Edited!');
      }).catch((error: any) => {
        alert.error("ERRO AO MUDAR ACESSO!");
        console.log('Error updating the document:', error);
      });

      await db.collection('users').doc(uid).update({
        company: company.uid,
      }).catch((error: any) => {
        console.log('Error updating the document:', error);
      });

      await db.collection('companies').doc(company.uid).update({
        [`users.${documentWithoutFormatation}`]: !!value
      });

      const timestamp = firebase.firestore.FieldValue.serverTimestamp();
      await db.collection('logs').add({
        compayId: company.uid,
        userId: user.uid,
        collectionName: "companies",
        createdAt: timestamp,
        difference: {
          users: {
            [`${documentWithoutFormatation}`]: !!value
          }
        }
      });

    } catch (e) {
      alert.error("ERRO AO ALTERAR PERMISSÃO");
    }
  }

  async function handleSubmit() {
    let Format: Record<string, boolean> = {};
    const dataCompany = await GetCompany(user.company);
    const companyUsers = (dataCompany as any)?.users as Record<string, boolean> | undefined;

    // Reconstrói o mapa de usuários, trocando a chave velha (sem formatação antiga) pela nova (também sem formatação)
    for (const data in (companyUsers || {})) {
      if (data !== documentWithoutFormatation) {
        Format = {
          ...Format,
          [data]: companyUsers![data],
        };
      } else {
        Format = {
          ...Format,
          // a chave deve ser SEM formatação
          [documentWithoutFormatation]: Boolean(user?.access?.delivery),
        };
      }
    }

    db.collection('users').doc(user.uid).update({
      email: email,
      name: name,
    }).then(() => {
      console.log('Profile Successfully Edited!');
      alert.success('Alterado com sucesso');
    }).catch((error: any) => {
      console.log('Error updating the document:', error);
      alert.error('Erro ao atualizar');
    });

    if (company?.uid) {
      db.collection('companies').doc(company.uid).update({
        users: Format,
      }).then(() => {
        console.log('Ok');
      }).catch((error: any) => {
        console.log('Erro ao remover', error);
      });
    }

    if (user.email !== email) {
      axios.put(
        Links.changeEmail,
        { id: user.uid, newEmail: email }
      ).then(() => {
        alert.success('Email atualizado com sucesso!');
      }).catch(e => {
        console.log(e);
        alert.error('Erro ao atualizar o email');
      });
    }
  }

  async function handleRemove() {
    if (!company?.uid) {
      alert.error('Empresa inválida.');
      return;
    }

    let Format: Record<string, boolean> = {};

    const waybillsSnapshot = await db.collection('waybills').where('company', '==', company.uid).get();
    const waybillsByCompanyData = waybillsSnapshot.docs.map(doc => doc.data() as any);
    const driverInRomaneio = waybillsByCompanyData.find(waybill => waybill.driver === user.name && waybill.status !== 4);

    if (!!driverInRomaneio) {
      alert.error('Não foi possível excluir o motorista porque ele está associado ao romaneio');
      return;
    }

    for (const data in (company.users || {})) {
      if (data !== documentWithoutFormatation) {
        Format = {
          ...Format,
          [data]: (company.users as any)[data],
        };
      }
    }

    if (!!deleteUserOnCompany) {
      // deleteUserOnCompany(documentWithoutFormatation);
    }

    const timestamp = firebase.firestore.FieldValue.serverTimestamp();

    if (!!user.uid) {
      db.collection('users').doc(user.uid).update({ deletedAt: timestamp }).then(() => {
        alert.success('Removido com sucesso');
      }).catch((error: any) => {
        alert.error('Erro ao remover');
      });
    }

    db.collection('companies').doc(company.uid).update({
      users: Format,
    }).then(() => {
      console.log('Ok');
      if (user.uid === undefined) alert.success('Removido com sucesso');
    }).catch((error: any) => {
      alert.error('Erro ao remover');
    });

    if (userInfo) {
      await db.collection('logs').add({
        compayId: company.uid,
        userId: userInfo.uid,
        collectionName: "companies",
        createdAt: timestamp,
        difference: {
          users: {
            [documentWithoutFormatation]: false,
          },
        }
      });
    }
  }

  const isDriver = user.role === 'driver';
  const userLogin = isDriver ? (user?.phoneNumber ?? '') : (user?.email ?? '');
  const userDocumentFormatted = MaskCPFCNPJ(documentWithoutFormatation);

  return (
    <>
      {changeItem ?
        <textarea value={name} onChange={(event) => setName(event.target.value)} />
        :
        <span>{user.name ?? ''}</span>
      }

      <span className="bigContent">{userDocumentFormatted}</span>

      {changeItem ?
        <textarea
          value={email}
          onChange={(e: any) => {
            setEmail(e.target.value);
          }} />
        :
        <span className="bigContent">{userLogin}</span>
      }

      <span style={{ paddingLeft: 12 }}>{user.role}</span>

      <div
        className={toggleCheckBox ? ' label-active ' : 'containerCheckbox'}
        style={{ color: 'var(--secondary)' }}
      >
        <input
          type="checkbox"
          onClick={() => {
            // if (user?.access?.delivery === undefined) {
            //   alert.error('Este usuário necessita terminar o cadastro no aplicativo!')
            //   return;
            // };
            handleAccess(user.uid, 1);
          }}
        />
        {toggleCheckBox && (
          <img
            src={checkbox}
            alt="checkbox true"
            onClick={() => handleAccess(user.uid, 0)}
          />
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '2rem' }}>
        {!isDriver &&
          <StyledResetPassword
            style={{ background: color, width: 135 }}
            uid={user.uid}
            collection='users'
          >
            <span>Redefinir senha</span>
          </StyledResetPassword>
        }

        <div
          className="blockIconButtons"
          style={{
            display: "flex",
            paddingLeft: 15,
            justifyContent: "space-between",
            gap: '1rem',
            fontSize: '1.3rem',
            alignItems: "center",
            color: "var(--secondary)"
          }}>
          {!changeItem && !isDriver &&
            <BsPencilSquare
              style={{ cursor: 'pointer', marginRight: '0.2rem' }}
              onClick={() => setChangeItem(!changeItem)}
            />
          }

          {changeItem ? (
            <>
              <div className="buttonsContainer" style={{ display: "block", alignItems: "flex-start" }}>
                <div
                  className="sendButtonAccounts"
                  onClick={() => {
                    handleSubmit();
                    setChangeItem(!changeItem);
                  }}
                >
                  Salvar
                </div>
                <div className="cancelButtonAccounts" onClick={() => setChangeItem(!changeItem)}>
                  Cancelar
                </div>
              </div>
            </>
          ) : (
            <BsFillTrashFill
              style={{ cursor: 'pointer', zIndex: 999 }}
              onClick={() => {
                closeModalUsers();
                confirmAlert({
                  message: 'Deseja remover este usuário?',
                  buttons: [
                    { label: 'Cancelar', onClick: () => ('Cancelar') },
                    { label: 'Sim', onClick: () => { handleRemove(); } }
                  ],
                  overlayClassName: 'confirmAlert'
                });
              }}
            />
          )}
        </div>
      </div>
    </>
  );
}
